import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { endUserAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, Send, Loader2, Heart, Dumbbell } from "lucide-react";
import { toast } from "sonner";

const numOrEmpty = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : "";
};

export default function AppLogSessionPage() {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState({}); // itemId -> { completed, reps, loadKg }
  const [painLevel, setPainLevel] = useState(0);
  const [rpe, setRpe] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    endUserAPI.plans()
      .then((res) => {
        const found = (res.data.plans || []).find((p) => p.id === planId);
        setPlan(found || null);
        if (found) {
          const init = {};
          for (const it of found.items || []) {
            init[it.id] = { completed: true, reps: numOrEmpty(it.reps), loadKg: "" };
          }
          setRows(init);
        }
      })
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [planId]);

  const patch = (id, key, value) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const sets = (plan.items || [])
        .filter((it) => rows[it.id]?.completed)
        .map((it, idx) => {
          const r = rows[it.id];
          const set = {
            exercise_id: it.exercise?.id || "",
            plan_item_id: it.id,
            set_number: idx + 1,
            reps: Number(r.reps) || 0,
            completed: true,
          };
          if (r.loadKg !== "" && r.loadKg != null) set.load_kg = Number(r.loadKg);
          return set;
        });

      const payload = {
        exercise_plan_id: plan.id,
        pain_level: painLevel,
        notes,
        sets,
      };
      if (rpe !== "") payload.rpe = Number(rpe);

      await endUserAPI.createSession(payload);
      toast.success("Sesión registrada 💪");
      navigate("/app/historial", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || "No pudimos registrar la sesión");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate("/app")} className="flex items-center text-sm text-muted-foreground">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          No encontramos este plan.
        </CardContent></Card>
      </div>
    );
  }

  const items = plan.items || [];

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center text-sm text-muted-foreground" data-testid="app-back">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div>
        <h1 className="text-lg font-bold text-foreground">Registrar sesión</h1>
        <p className="text-sm text-muted-foreground">{plan.title}</p>
      </div>

      {/* Ejercicios */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Dumbbell className="w-4 h-4 text-primary" />Ejercicios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {items.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Este plan no tiene ejercicios cargados; igual puedes registrar tu dolor y notas.
            </p>
          )}
          {items.map((it) => {
            const r = rows[it.id] || {};
            return (
              <div key={it.id} className={`rounded-lg border p-3 transition-colors ${r.completed ? "bg-primary/5 border-primary/30" : "bg-muted/30 border-border"}`}>
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={!!r.completed}
                    onCheckedChange={(v) => patch(it.id, "completed", !!v)}
                    data-testid="app-set-completed"
                  />
                  <p className="flex-1 text-sm font-medium">{it.exercise?.name || "Ejercicio"}</p>
                </div>
                {r.completed && (
                  <div className="flex gap-2 mt-2 ml-7">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Reps</Label>
                      <Input
                        type="number" inputMode="numeric"
                        value={r.reps}
                        onChange={(e) => patch(it.id, "reps", e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Carga (kg)</Label>
                      <Input
                        type="number" inputMode="decimal"
                        value={r.loadKg}
                        onChange={(e) => patch(it.id, "loadKg", e.target.value)}
                        placeholder="—"
                        className="h-9"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Cómo te sentiste */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Heart className="w-4 h-4 text-secondary" />¿Cómo te sentiste?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Dolor hoy: {painLevel}/10</Label>
            <Slider value={[painLevel]} onValueChange={([v]) => setPainLevel(v)} min={0} max={10} step={1} className="mt-2" data-testid="app-pain-slider" />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>Sin dolor</span><span>Dolor máximo</span>
            </div>
          </div>
          <div>
            <Label htmlFor="rpe" className="text-xs">Esfuerzo percibido (RPE 1-10) — opcional</Label>
            <Input id="rpe" type="number" inputMode="numeric" min={1} max={10}
              value={rpe} onChange={(e) => setRpe(e.target.value)} placeholder="—" className="h-9" />
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="¿Cómo te fue?" />
          </div>
        </CardContent>
      </Card>

      <Button className="w-full rounded-full" onClick={submit} disabled={submitting} data-testid="app-submit-session">
        {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
        {submitting ? "Registrando..." : "Registrar sesión"}
      </Button>
    </div>
  );
}
